import React from 'react'
import ApexChart from 'react-apexcharts'
import {formatDateTime} from 'helpers/dates' // Import the helper function
import {colors} from 'helpers/style'

const TemperatureBars = ({targetsview}) => {
  // Extract the data for the chart
  const {tp_timestamp, tp_min, tp_max, tp_mean, tp_ext_temp} = targetsview || {}

  // Helper function to check if a value is a valid number
  const isValidNumber = v => typeof v === 'number' && !isNaN(v)

  // Helper function to convert Celsius to Fahrenheit
  const celsiusToFahrenheit = celsius => (isValidNumber(celsius) ? (celsius * 9) / 5 + 32 : null)

  // Helper function to convert Fahrenheit to Celsius
  const fahrenheitToCelsius = fahrenheit => (isValidNumber(fahrenheit) ? ((fahrenheit - 32) * 5) / 9 : null)

  // Defensive: Only include valid numbers, otherwise null
  const tempDataF = [
    celsiusToFahrenheit(tp_max),
    celsiusToFahrenheit(tp_mean),
    celsiusToFahrenheit(tp_min),
    celsiusToFahrenheit(tp_ext_temp)
  ]

  // If all values are null/undefined, show a placeholder
  const hasValidData = tempDataF.some(isValidNumber)

  // Define custom colors for each bar
  const barColors = [colors.chart.maximum, colors.chart.mean, colors.chart.minimum, colors.chart.ambient]

  // Defensive: filter out nulls for min/max calculations
  const filteredDataF = tempDataF.filter(isValidNumber)
  const dataMin = filteredDataF.length ? Math.min(...filteredDataF) : 0
  const dataMax = filteredDataF.length ? Math.max(...filteredDataF) : 100
  const dataRange = dataMax - dataMin

  // Add 10% padding above and below
  const padding = dataRange * 0.1
  let yAxisMin = Math.max(0, dataMin - padding) // Use 0 if calculated min is positive
  let yAxisMax = dataMax + padding

  // If yAxisMin is 0 and dataMin is positive, ensure we still have some space below the bars
  if (yAxisMin === 0 && dataMin > 0) {
    yAxisMax = dataMax + dataRange * 0.15 // Add a bit more space at top to balance
  }

  // Calculate appropriate tick interval
  const range = yAxisMax - yAxisMin
  let tickAmount = 10 // Reduced target number of ticks for more spacing
  let tickInterval = range / tickAmount

  // Round tick interval to nice numbers
  const magnitude = Math.pow(10, Math.floor(Math.log10(tickInterval)))
  const normalizedTick = tickInterval / magnitude

  if (normalizedTick <= 1) tickInterval = magnitude
  else if (normalizedTick <= 2) tickInterval = 2 * magnitude
  else if (normalizedTick <= 5) tickInterval = 5 * magnitude
  else tickInterval = 10 * magnitude

  // For very small ranges, ensure minimum tick interval
  if (tickInterval < 10) tickInterval = 10

  // Adjust min and max to align with tick intervals
  yAxisMin = Math.floor(yAxisMin / tickInterval) * tickInterval
  yAxisMax = Math.ceil(yAxisMax / tickInterval) * tickInterval

  // Ensure yAxisMin is 0 if original calculation was positive
  if (dataMin > 0) yAxisMin = 0

  // Chart configuration
  const chartOptions = {
    chart: {
      type: 'bar',
      height: 350,
      toolbar: {
        show: false
      }
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
        endingShape: 'rounded',
        distributed: true // Enable distributed colors
      }
    },
    colors: barColors, // Apply colors at the root level
    dataLabels: {
      enabled: true,
      style: {
        fontSize: '14px',
        colors: ['#FFFFFF']
      },
      offsetY: 0,
      formatter: val => (isValidNumber(val) ? `${val.toFixed(1)}` : '--')
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent']
    },
    xaxis: {
      categories: ['Max', 'Mean', 'Min', 'Ambient'],
      title: {
        text: undefined
      },
      labels: {
        style: {
          fontSize: '12px',
          fontWeight: 'bold',
          color: 'black'
        }
      }
    },
    yaxis: {
      min: yAxisMin,
      max: yAxisMax,
      tickAmount: Math.ceil((yAxisMax - yAxisMin) / tickInterval),
      title: {
        text: 'Temperature (°F)',
        style: {
          color: '#333'
        }
      },
      labels: {
        show: true,
        formatter: val => (isValidNumber(val) ? `${val.toFixed(0)}°F` : '--'),
        style: {
          colors: ['#333']
        }
      }
    },
    fill: {
      opacity: 1
    },
    title: {
      text: `Temperature @ ${tp_timestamp ? formatDateTime(tp_timestamp) : '--'}`,
      align: 'left',
      margin: 0,
      offsetX: 0,
      offsetY: 0,
      floating: false,
      style: {
        fontSize: '20px',
        fontWeight: 'bold',
        fontFamily: undefined,
        color: 'black'
      }
    },
    tooltip: {
      y: {
        formatter: val => {
          if (!isValidNumber(val)) return '--'
          const celsius = fahrenheitToCelsius(val)
          return `${val.toFixed(1)}°F (${isValidNumber(celsius) ? celsius.toFixed(1) : '--'}°C)`
        }
      }
    }
  }

  // Chart data series
  const chartSeries = [
    {
      name: 'Temperature',
      data: tempDataF.map(v => (isValidNumber(v) ? v : null))
    }
  ]

  if (!hasValidData) {
    return <div style={{textAlign: 'center', color: 'gray', padding: '2rem'}}>No temperature data available</div>
  }

  return (
    <div>
      <ApexChart options={chartOptions} series={chartSeries} type="bar" height={270} />
    </div>
  )
}

export default TemperatureBars
